import { useTranslation } from 'react-i18next';
import InputField from '../../../../components/ui/InputField';
import AssetDetailSection from '../../../assets/components/AssetDetailSection';
import type { StationCreationForm } from '../stationCreationForm';

interface HouseStepProps {
    form: StationCreationForm;
    update: <K extends keyof StationCreationForm>(key: K, value: StationCreationForm[K]) => void;
}

/**
 * Step 2 of the house variant — the house itself: a name and a description.
 *
 * No positions, no lock and no objects: a house holds rooms and has no doors,
 * and its rooms are created one by one through room creation, each in its own
 * cell (plan `room-houses-and-combining-rooms.md` §4 rows 1–2, A1 h). Naming
 * follows the station step: a new location was named on step 1 and IS this
 * house; at an existing location the house carries its own name.
 */
const HouseStep = ({ form, update }: HouseStepProps) => {
    const { t } = useTranslation();

    return (
        <AssetDetailSection
            title={t('house_create.house.title')}
            subtitle={t('house_create.house.subtitle')}
        >
            <div className={'flex flex-col gap-5'}>
                {form.locationMode === 'existing' ? (
                    <InputField
                        label={t('house_create.house.name_label')}
                        placeholder={t('house_create.house.name_placeholder')}
                        value={form.stationName}
                        onChange={(v) => update('stationName', v)}
                    />
                ) : (
                    <p className={'text-sm text-foreground/60'}>
                        {t('house_create.house.name_from_location', {
                            name: form.newLocationName.trim(),
                        })}
                    </p>
                )}

                <InputField
                    labelAbove
                    label={t('house_create.house.description_label')}
                    placeholder={t('house_create.house.description_placeholder')}
                    value={form.description}
                    onChange={(v) => update('description', v)}
                />
            </div>
        </AssetDetailSection>
    );
};

export default HouseStep;
